import React from "react";
import { useParams, Link } from "react-router-dom";
import styled from "styled-components";
import MarkdownArticle from "./MarkdownArticle";
import { useGetArticleQuery } from "../../services/rtkApi/agentApi";
import { useAppSelector } from "../../store/hooks";
import { isWriter } from "../authentication/RequireWriterRoute";
import { checkRole } from "../recipe_page/recipe_page";

const BlogArticlePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { data: article, isLoading, isError } = useGetArticleQuery(slug || "", {
    skip: !slug,
  });
  const auth = useAppSelector((state) => state.auth);
  // admins may edit anything; a writer only their own posts (the backend enforces the same)
  const canEdit =
    isWriter(auth.role) &&
    (checkRole(auth.role ?? "", "admin") ||
      (article?.authorType === "user" && article.authorUsername === auth.username));

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingText>loading article...</LoadingText>
      </PageContainer>
    );
  }

  if (isError || !article) {
    return (
      <PageContainer>
        <LoadingText>article not found</LoadingText>
        <BackLink to="/blog">back to blog</BackLink>
      </PageContainer>
    );
  }

  const date = new Date(article.publishedAt);
  const formattedDate = date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <PageContainer>
      <ArticleContainer>
        <BackLink to="/blog">&larr; back to blog</BackLink>

        {article.book && (
          <BookBanner>
            <BookBannerLabel>
              {article.chapterIndex
                ? `chapter ${article.chapterIndex} of `
                : "part of "}
            </BookBannerLabel>
            <BookBannerLink to={`/blog/books/${article.book.slug}`}>
              {article.book.title}
            </BookBannerLink>
          </BookBanner>
        )}

        <ArticleHeader>
          <MetaRow>
            <CategoryTag>{article.category}</CategoryTag>
            <DateText>{formattedDate}</DateText>
            <AuthorText>by {article.author?.label || `${article.agentName} agent`}</AuthorText>
          </MetaRow>
          <ArticleTitle>{article.title}</ArticleTitle>
          {article.subtitle && (
            <ArticleSubtitle>{article.subtitle}</ArticleSubtitle>
          )}
        </ArticleHeader>

        <Divider />

        <MarkdownArticle content={article.content || ""} />

        {article.tags.length > 0 && (
          <>
            <Divider />
            <TagList>
              {article.tags.map((tag) => (
                <Tag key={tag}>{tag}</Tag>
              ))}
            </TagList>
          </>
        )}

        {article.wayback?.status === "captured" && article.wayback.snapshotUrl && (
          <ArchiveNote>
            archived on the Internet Archive
            {article.wayback.capturedAt
              ? ` — ${new Date(article.wayback.capturedAt).toLocaleDateString()}`
              : ""}{" "}
            (
            <ArchiveLink
              href={article.wayback.snapshotUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              view snapshot
            </ArchiveLink>
            )
          </ArchiveNote>
        )}

        {article.book && (article.previousChapter || article.nextChapter) && (
          <ChapterNav>
            {article.previousChapter ? (
              <ChapterNavLink to={`/blog/${article.previousChapter.slug}`} $align="left">
                <ChapterNavLabel>← previous chapter</ChapterNavLabel>
                <ChapterNavTitle>{article.previousChapter.title}</ChapterNavTitle>
              </ChapterNavLink>
            ) : (
              <span />
            )}
            {article.nextChapter ? (
              <ChapterNavLink to={`/blog/${article.nextChapter.slug}`} $align="right">
                <ChapterNavLabel>next chapter →</ChapterNavLabel>
                <ChapterNavTitle>{article.nextChapter.title}</ChapterNavTitle>
              </ChapterNavLink>
            ) : (
              <span />
            )}
          </ChapterNav>
        )}

        <BottomNav>
          <BackLink to="/blog">&larr; back to blog</BackLink>
          {article.book && (
            <EditLink to={`/blog/books/${article.book.slug}`}>
              all chapters of “{article.book.title}”
            </EditLink>
          )}
          {canEdit && <EditLink to={`/blog/manage/${article._id}`}>edit this post</EditLink>}
        </BottomNav>
      </ArticleContainer>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  min-height: 100vh;
  background-color: #f0f0f0;
  padding: 20px;

  @media (max-width: 700px) {
    padding: 12px;
  }
`;

const ArticleContainer = styled.article`
  max-width: 720px;
  margin: 0 auto;
`;

const BackLink = styled(Link)`
  display: inline-block;
  color: #466a5d;
  text-decoration: none;
  font-size: 0.9rem;
  margin-bottom: 20px;
  padding: 4px 0;
  transition: color 0.15s;

  &:hover {
    color: #2a4a3d;
    text-decoration: underline;
  }
`;

const ArticleHeader = styled.div`
  margin-bottom: 20px;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const CategoryTag = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #466a5d;
  background: #d4f9eb;
  padding: 2px 8px;
  border: 1px solid #466a5d;
`;

const DateText = styled.span`
  font-size: 0.85rem;
  color: #888;
`;

const AuthorText = styled.span`
  font-size: 0.85rem;
  color: #999;
  font-style: italic;
`;

const ArticleTitle = styled.h1`
  font-size: 2.2rem;
  color: #222;
  margin: 0 0 6px;
  line-height: 1.2;
  text-shadow: 1px 1px 3px #3c3c3c30;

  @media (max-width: 700px) {
    font-size: 1.7rem;
  }
`;

const ArticleSubtitle = styled.p`
  font-size: 1.1rem;
  color: #555;
  margin: 0;
  line-height: 1.4;
  font-style: italic;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #ddd;
  margin: 24px 0;
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Tag = styled.span`
  font-size: 0.75rem;
  color: #777;
  background: #f5f5f5;
  padding: 3px 10px;
  border: 1px solid #ddd;
`;

const BottomNav = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  flex-wrap: wrap;
  margin-top: 32px;
  padding-bottom: 40px;
`;

const EditLink = styled(Link)`
  color: #466a5d;
  font-size: 0.9rem;
  text-decoration: underline;

  &:hover {
    color: #2a4a3d;
  }
`;

const BookBanner = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  flex-wrap: wrap;
  background: #f3edfa;
  border: 1px solid #d9c9ec;
  padding: 8px 14px;
  margin-bottom: 16px;
  font-size: 0.85rem;
`;

const BookBannerLabel = styled.span`
  color: #6a4d8a;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-size: 0.72rem;
  font-weight: 700;
`;

const BookBannerLink = styled(Link)`
  color: #4a3563;
  font-weight: 600;
  text-decoration: underline;
`;

const ChapterNav = styled.nav`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 28px;
`;

const ChapterNavLink = styled(Link)<{ $align: "left" | "right" }>`
  display: flex;
  flex-direction: column;
  gap: 3px;
  text-align: ${(p) => p.$align};
  background: white;
  border: 1px solid #ccc;
  padding: 12px 16px;
  text-decoration: none;
  box-shadow: 1px 2px rgba(0, 0, 0, 0.1);
  transition: all 0.15s;

  &:hover {
    box-shadow: 2px 3px rgba(0, 0, 0, 0.2);
    border-color: #466a5d;
  }
`;

const ChapterNavLabel = styled.span`
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #888;
`;

const ChapterNavTitle = styled.span`
  font-size: 0.92rem;
  color: #222;
  line-height: 1.35;
`;

const ArchiveNote = styled.p`
  margin-top: 24px;
  font-size: 0.82rem;
  color: #888;
  font-style: italic;
`;

const ArchiveLink = styled.a`
  color: #466a5d;
  text-decoration: underline;
`;

const LoadingText = styled.p`
  text-align: center;
  color: #888;
  font-size: 1rem;
  padding: 60px 0;
`;

export default BlogArticlePage;
